import axios from 'axios'
import { API_CONFIG } from './config'
import { gerenciadorLoading } from './loading'
import { endpoints } from './endpoints'
import { useEmpresaStore } from '@/stores/empresa'

async function redirecionarParaLoginSeRotaProtegida() {
    try {
        const { default: router } = await import('@/router')
        const { useAutenticacaoStore } = await import('@/stores/autenticacao')

        const rotaAtual = router.currentRoute.value
        const rotaRequerAutenticacao = rotaAtual.matched.some((record) => {
            return record.meta.requerAutenticacao
        })

        if (!rotaRequerAutenticacao || rotaAtual.name === 'Login') {
            return
        }

        const idioma = rotaAtual.params.locale || rotaAtual.path.split('/')[1] || 'pt'
        const autenticacaoStore = useAutenticacaoStore()
        autenticacaoStore.encerrarSessao()

        router.replace({
            path: `/${idioma}/login`,
            query: { redirect: rotaAtual.fullPath },
        }).catch(() => {})
    } catch (erro) {
        console.error('Erro ao redirecionar para login:', erro)
    }
}

/**
 * Serviço principal de API
 * Responsável por gerenciar todas as requisições HTTP do sistema
 * Implementa padrão Singleton para garantir uma única instância
 */
class ApiService {
    /**
     * Inicializa o serviço de API com as configurações base
     * Cria uma instância do axios com as configurações do ambiente atual
     */
    constructor() {
        this.api = axios.create({
            baseURL: API_CONFIG.baseUrl,
            timeout: API_CONFIG.timeout,
        })

        this.configurarInterceptors()
    }

    /**
     * Configura os interceptors do axios para tratar requisições e respostas
     * Gerencia automaticamente o estado de loading e tokens de autenticação
     */
    configurarInterceptors() {
        this.api.interceptors.request.use(
            (config) => {
                gerenciadorLoading.iniciar()

                if (!this.ehRotaPublica(config.url)) {
                    this.adicionarTokenAutorizacao(config)
                }

                return config
            },
            this.tratarErroRequisicao
        )

        this.api.interceptors.response.use(
            this.tratarRespostaSucesso,
            this.tratarErroResposta
        )
    }

    /**
     * Verifica se uma URL específica é uma rota pública
     * @param {string} url - URL da requisição
     * @returns {boolean} - Verdadeiro se for rota pública
     */
    ehRotaPublica(url) {
        const urlRelativa = url.replace(this.api.defaults.baseURL, '')
        return API_CONFIG.rotasPublicas.some((rota) => {
            return urlRelativa === rota || (rota.endsWith('/') && urlRelativa.startsWith(rota))
        })
    }

    /**
     * Adiciona o token de autorização ao header da requisição
     * @param {Object} config - Configuração da requisição
     */
    adicionarTokenAutorizacao(config) {
        const autenticacao = JSON.parse(localStorage.getItem('autenticacao'))

        if (!config.headers) {
            config.headers = {}
        }

        if (autenticacao) {
            config.headers.Authorization = `Bearer ${autenticacao.token}`
        }

        try {
            const empresaStore = useEmpresaStore()
            const tenantId = empresaStore.empresaAtual?.tenant_id

            if (tenantId && !config.headers['X-Tenant-UUID']) {
                config.headers['X-Tenant-UUID'] = tenantId
            }
        } catch (e) {
            console.warn("Store de empresa ainda não disponível para o header de Tenant")
        }
    }

    /**
     * Trata respostas bem-sucedidas da API
     * @param {Object} response - Resposta da requisição
     * @returns {Object} - Resposta processada
     */
    tratarRespostaSucesso(response) {
        gerenciadorLoading.finalizar()
        return response
    }

    /**
     * Trata erros nas respostas da API
     * Inclui tratamento específico para diferentes códigos HTTP
     * @param {Error} error - Erro da requisição
     */
    tratarErroResposta(error) {
        gerenciadorLoading.finalizar()

        // Tratamento específico para diferentes tipos de erro
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    redirecionarParaLoginSeRotaProtegida()
                    break
                case 403:
                    // Tratar erro de autorização
                    break
                case 404:
                    // Tratar erro de recurso não encontrado
                    break
                case 500:
                    // Tratar erro interno do servidor
                    break
            }
        }

        return Promise.reject(error)
    }

    /**
     * Trata erros nas requisições da API
     * @param {Error} error - Erro da requisição
     */
    tratarErroRequisicao(error) {
        gerenciadorLoading.finalizar()
        return Promise.reject(error)
    }

    /**
     * Configura headers específicos para upload de arquivos
     * @param {Object} config - Configuração base
     * @returns {Object} - Configuração com headers de upload
     */
    configurarHeadersUpload(config = {}) {
        return {
            ...config,
            headers: {
                ...config.headers,
                'Content-Type': 'multipart/form-data',
            },
        }
    }

    /**
     * Método principal para realizar requisições HTTP
     * @param {string} identificadorEndpoint - Identificador do endpoint no arquivo endpoints.js
     * @param {Object} data - Dados da requisição (opcional)
     * @param {Object} config - Configurações adicionais (opcional)
     * @returns {Promise} - Promise com a resposta da requisição
     */
    async requisicao(identificadorEndpoint, data = null, config = {}) {
        // Obtém a configuração do endpoint
        const endpointConfig = endpoints[identificadorEndpoint]

        if (!endpointConfig) {
            throw new Error(`Endpoint '${identificadorEndpoint}' não encontrado`)
        }

        let { url, metodo, timeout } = endpointConfig

        const timeoutFinal = config.timeout !== undefined
            ? config.timeout
            : (timeout || API_CONFIG.timeout);

        const configFinal = {
            ...config,
            timeout: timeoutFinal
        }

        if (configFinal['paramUrl']) {
            url += configFinal['paramUrl']['id'];
        }

        try {
            let response

            switch (metodo.toLowerCase()) {
                case 'get':
                    response = await this.api.get(url, configFinal)
                    break
                case 'post':
                    response = await this.api.post(url, data, configFinal)
                    break
                case 'put':
                    response = await this.api.put(url, data, configFinal)
                    break
                case 'delete':
                    response = await this.api.delete(url, configFinal)
                    break
                default:
                    throw new Error(`Método HTTP '${metodo}' não suportado`)
            }

            return response.data
        } catch (error) {
            throw error
        }
    }
}

export const apiService = new ApiService()
